import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  Avatar,
  Divider,
  Tooltip,
  Badge,
  Popover,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Menu,
  LayoutDashboard,
  Globe,
  LogOut,
  Coins,
  MessageCircle,
  Play,
  Mail,
  Bell,
  X,
  Info,
  Smartphone,
  User,
  Shield,
  ShieldCheck,
  Gift,
  Flag,
  Cloud,
  HelpCircle
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import logoPainel from '../imagens/logopainelftm.png.ico';
import trackEvent from '../utils/trackEvent';

const drawerWidth = 280;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    boxSizing: 'border-box',
    background: '#141926',
    color: '#fff',
    borderRight: '1px solid rgba(255,255,255,0.06)',
    boxShadow: '4px 0 24px rgba(0, 0, 0, 0.2)'
  },
}));

const StyledListItem = styled(ListItem)(({ theme, selected }) => ({
  margin: '8px 16px',
  borderRadius: '12px',
  background: selected 
    ? 'rgba(124, 58, 237, 0.15)' 
    : 'transparent',
  color: selected ? '#7C3AED' : '#fff',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    background: 'rgba(124, 58, 237, 0.1)',
  },
  '& .MuiListItemIcon-root': {
    color: selected ? '#7C3AED' : '#fff',
    transition: 'transform 0.3s ease',
  },
  '&:hover .MuiListItemIcon-root': {
    transform: 'scale(1.1)',
  },
  '& .MuiListItemText-root': {
    color: selected ? '#7C3AED' : '#fff',
  }
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  width: '100%',
  marginLeft: 0,
  [theme.breakpoints.up('sm')]: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
  },
  background: 'rgba(17, 24, 39, 0.8)',
  backdropFilter: 'blur(10px)',
  color: '#fff',
  boxShadow: 'none',
  borderBottom: '1px solid rgba(124, 58, 237, 0.2)'
}));

const TokenDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  borderRadius: '12px',
  background: 'rgba(124, 58, 237, 0.15)',
  border: '1px solid rgba(124, 58, 237, 0.2)',
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 48,
  height: 48,
  background: 'transparent',
  border: 'none',
  boxShadow: 'none',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: 'none',
  }
}));

const Layout = ({ children, onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [dismissedNotifs, setDismissedNotifs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dismissedNotifs') || '[]'); } catch { return []; }
  });
  const [dismissedBanner, setDismissedBanner] = useState(() => localStorage.getItem('dismissedDomainBanner') === '1');
  const [showBmTutorial, setShowBmTutorial] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    if (!localStorage.getItem('seenBmTutorial2026')) {
      const t = setTimeout(() => setShowBmTutorial(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  const handleCloseBmTutorial = () => {
    setShowBmTutorial(false);
    localStorage.setItem('seenBmTutorial2026', '1');
  };
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const baseMenuItems = [
    { text: 'Dashboard', icon: <LayoutDashboard size={20} strokeWidth={1.75} />, path: '/dashboard' },
    { text: 'Meus Sites', icon: <Globe size={20} strokeWidth={1.75} />, path: '/meus-sites' },
    { text: 'FTM Legacy', icon: <Shield size={20} strokeWidth={1.75} />, path: '/ftm-legacy' },
    { text: 'Meus Emails', icon: <Mail size={20} strokeWidth={1.75} />, path: '/meus-emails' },
    { text: 'Números Virtuais', icon: <Smartphone size={20} strokeWidth={1.75} />, path: '/numero-virtual' },
    { text: 'Vídeo Aulas', icon: <Play size={20} strokeWidth={1.75} />, path: '/video-aulas' },
    { text: 'Dúvidas Frequentes', icon: <HelpCircle size={20} strokeWidth={1.75} />, path: '/duvidas-frequentes' },
    { text: 'Comprar Tokens', icon: <Coins size={20} strokeWidth={1.75} />, path: '/comprar-tokens' },
    { text: 'Indique e Ganhe', icon: <Gift size={20} strokeWidth={1.75} />, path: '/indique-ganhe' }
  ];

  const adminMenuItems = [
    { text: 'Criar Verificaix', icon: <ShieldCheck size={20} strokeWidth={1.75} />, path: '/criar-verificaix' },
    { text: 'Verificaix V2', icon: <ShieldCheck size={20} strokeWidth={1.75} />, path: '/criar-verificaix-v2' },
    { text: 'GRINGA', icon: <Flag size={20} strokeWidth={1.75} />, path: '/gringa' },
    { text: 'Sites Cloudflare', icon: <Cloud size={20} strokeWidth={1.75} />, path: '/cloudflare-sites' }
  ];

  const menuItems = user?.tipo === 'admin'
    ? [...baseMenuItems, ...adminMenuItems]
    : baseMenuItems;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const notifications = [
    {
      id: 'meta-tag-instabilidade-abril2026',
      title: 'Instabilidade em verificação de domínio',
      message: 'META TAG não está funcionando (Problemas na própria META independente de domínio)',
      date: '02/04/2026',
    },
    {
      id: 'dominio-proprio-recomendacao',
      title: 'Recomendamos usar domínio próprio',
      message: 'A Meta está com instabilidade na verificação de domínios. Recomendamos que você configure um domínio próprio para evitar problemas. Temos uma vídeo aula explicando como fazer na página de Vídeo Aulas.',
      date: '23/02/2026',
      action: '/video-aulas',
      actionLabel: 'Ver Vídeo Aulas'
    },
    {
      id: 'email-feature',
      title: 'Nova função: Caixa de Entrada',
      message: 'Agora você pode receber emails da META diretamente no painel! Use o email do seu site para verificar sua BM. Acesse em "Meus Emails" no menu lateral.',
      date: '10/03/2026',
      action: '/meus-emails',
      actionLabel: 'Ir para Meus Emails'
    }
  ];
  const activeNotifs = notifications.filter(n => !dismissedNotifs.includes(n.id));
  const notifCount = activeNotifs.length;

  const handleDismissNotif = (id) => {
    const updated = [...dismissedNotifs, id];
    setDismissedNotifs(updated);
    localStorage.setItem('dismissedNotifs', JSON.stringify(updated));
  };

  const handleDismissBanner = () => {
    setDismissedBanner(true);
    localStorage.setItem('dismissedDomainBanner', '1');
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
    }
  };

  const drawer = (
    <div>
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        {user.foto ? (
          <UserAvatar src={user.foto}>
            {user.nome?.charAt(0)?.toUpperCase()}
          </UserAvatar>
        ) : (
          <img src={logoPainel} alt="Logo Painel FTM" style={{ width: 40, height: 40, borderRadius: 8, display: 'block' }} />
        )}
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#fff' }}>
            {user.nome || 'Usuário'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {user.email}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <List>
        {menuItems.map((item) => (
          <Tooltip 
            key={item.text} 
            title={item.text} 
            placement="right"
            arrow
          >
            <StyledListItem 
              button 
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
              selected={location.pathname === item.path}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </StyledListItem>
          </Tooltip>
        ))}
        <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
        <Tooltip title="Sair" placement="right" arrow>
          <StyledListItem button onClick={handleLogout}>
            <ListItemIcon>
              <LogOut size={20} strokeWidth={1.75} />
            </ListItemIcon>
            <ListItemText primary="Sair" />
          </StyledListItem>
        </Tooltip>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', background: '#111827' }}>
      <StyledAppBar position="fixed">
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <Menu size={20} strokeWidth={1.75} />
          </IconButton>
          {location.pathname !== '/numero-virtual' && (
            <TokenDisplay>
              <span style={{ color: '#7C3AED', display: 'flex', alignItems: 'center' }}><Coins size={20} strokeWidth={1.75} /></span>
              <Typography variant="h6" sx={{ color: '#7C3AED', fontWeight: 600 }}>
                {user.tokens || 0} Tokens
              </Typography>
            </TokenDisplay>
          )}

          <Box sx={{ flex: 1 }} />

          <Tooltip title="Notificações">
            <IconButton
              onClick={(e) => setNotifAnchor(e.currentTarget)}
              sx={{ color: '#9CA3AF', '&:hover': { color: '#fff' } }}
            >
              <Badge badgeContent={notifCount} color="error"
                sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16, background: '#7C3AED' } }}>
                <Bell size={20} strokeWidth={1.75} />
              </Badge>
            </IconButton>
          </Tooltip>

          <Tooltip title="Minha Conta">
            <IconButton
              onClick={() => navigate('/minha-conta')}
              sx={{
                ml: 1, color: location.pathname === '/minha-conta' ? '#7C3AED' : '#9CA3AF',
                '&:hover': { color: '#fff' }
              }}
            >
              <User size={20} strokeWidth={1.75} />
            </IconButton>
          </Tooltip>

          <Popover
            open={Boolean(notifAnchor)}
            anchorEl={notifAnchor}
            onClose={() => setNotifAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { background: '#1F2937', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3, width: 360, maxHeight: 400, mt: 1 } }}
          >
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.95rem' }}>Notificações</Typography>
            </Box>
            {activeNotifs.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Bell size={24} style={{ color: '#4B5563', marginBottom: 8 }} />
                <Typography sx={{ color: '#6B7280', fontSize: '0.85rem' }}>Nenhuma notificação</Typography>
              </Box>
            ) : (
              activeNotifs.map(n => (
                <Box key={n.id} sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.04)', '&:hover': { background: 'rgba(124,58,237,0.04)' } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.85rem', flex: 1 }}>{n.title}</Typography>
                    <IconButton size="small" onClick={() => handleDismissNotif(n.id)} sx={{ color: '#6B7280', ml: 1, p: 0.25 }}>
                      <X size={14} />
                    </IconButton>
                  </Box>
                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem', lineHeight: 1.5, mb: 1 }}>{n.message}</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ color: '#4B5563', fontSize: '0.7rem' }}>{n.date}</Typography>
                    {n.action && (
                      <Button size="small" onClick={() => { navigate(n.action); setNotifAnchor(null); }}
                        sx={{ color: '#7C3AED', textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, p: 0 }}>
                        {n.actionLabel}
                      </Button>
                    )}
                  </Box>
                </Box>
              ))
            )}
          </Popover>
        </Toolbar>
      </StyledAppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              background: '#141926',
              color: '#fff'
            },
          }}
        >
          {drawer}
        </Drawer>
        <StyledDrawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
          }}
          open
        >
          {drawer}
        </StyledDrawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: '64px',
          background: '#111827',
          minHeight: '100vh',
          position: 'relative'
        }}
      >
        {!dismissedBanner && location.pathname !== '/numero-virtual' && (
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 2, p: 1.5, px: 2, mb: 2, borderRadius: 2,
            background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.25)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
              <Info size={18} style={{ color: '#A78BFA', flexShrink: 0 }} />
              <Typography sx={{ color: '#D1D5DB', fontSize: '0.82rem' }}>
                O domínio <strong style={{ color: '#A78BFA' }}>painelftm.com.br</strong> será alterado para{' '}
                <strong style={{ color: '#7C3AED' }}>FTMPAINEL.COM.BR</strong> em breve. Atualize seus favoritos!
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleDismissBanner} sx={{ color: '#6B7280', flexShrink: 0 }}>
              <X size={16} />
            </IconButton>
          </Box>
        )}
        {children}
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 32 },
            right: { xs: 16, sm: 32 },
            zIndex: 2000
          }}
        >
          <Tooltip title="Fale conosco no WhatsApp" placement="left" arrow>
            <IconButton
              sx={{
                background: '#25D366',
                color: '#fff',
                width: { xs: 48, sm: 64 },
                height: { xs: 48, sm: 64 },
                boxShadow: '0 4px 24px rgba(37,211,102,0.3)',
                '&:hover': { background: '#128C7E' }
              }}
              href="https://wa.me/5567933005660"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('Contact', { contentName: 'WhatsApp' })}
            >
              <MessageCircle size={32} strokeWidth={1.75} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Dialog
        open={showBmTutorial}
        onClose={handleCloseBmTutorial}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: '#0F1219',
            border: '1px solid rgba(124,58,237,0.25)',
            borderRadius: 3,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, rgba(124,58,237,0.15), rgba(124,58,237,0.05))',
          borderBottom: '1px solid rgba(124,58,237,0.2)',
          py: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: 2,
              background: 'rgba(124,58,237,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Shield size={18} strokeWidth={2} color="#A78BFA" />
            </Box>
            <Box>
              <Typography sx={{ color: '#F9FAFB', fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}>
                Novo método de verificação de BM em 2026
              </Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.78rem', mt: 0.3 }}>
                Assista antes de começar — método atualizado
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCloseBmTutorial} size="small" sx={{ color: '#6B7280' }}>
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, background: '#000' }}>
          <Box sx={{
            position: 'relative',
            width: '100%',
            paddingTop: '56.25%',
          }}>
            <iframe
              src="https://www.youtube.com/embed/aAJaeVKxCwg?rel=0&modestbranding=1&autoplay=1"
              title="Novo método de verificação de BM em 2026"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0, left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{
          background: '#0F1219',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          py: 1.5, px: 3,
          justifyContent: 'space-between',
        }}>
          <Typography sx={{ color: '#6B7280', fontSize: '0.8rem' }}>
            Você pode rever esta aula em "Vídeo Aulas" no menu
          </Typography>
          <Button
            variant="contained"
            onClick={handleCloseBmTutorial}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              background: '#7C3AED',
              '&:hover': { background: '#6D28D9' },
              px: 3,
            }}
          >
            Entendi, vamos começar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Layout; 